export type MirrorSkipReason =
  | 'operation_not_mirrored'
  | 'unexpected_variables'
  | 'gym_type_not_mirrored'
  | 'no_hyrox_gym'
  | 'equipment_untranslatable'
  | 'no_one_fiit_instance'
  | 'no_active_hyrox_instance'
  | 'ambiguous_hyrox_instance';

export type MirrorVariables = Record<string, unknown>;

export type TranslateResult = { ok: true; variables: MirrorVariables } | { ok: false; reason: MirrorSkipReason };

export type HyroxInstanceMatch =
  | { found: true; id: string }
  | { found: false; reason: 'no_active_hyrox_instance' | 'ambiguous_hyrox_instance' };

// Int PKs are reconciled by natural key during the cutover (users by email, gyms by externalId, equipment
// by name), so the ids one-fiit hands the app are not hyrox's. Content ids (lsn_/prp_) are identical.
export interface MirrorLookups {
  getHyroxGymId: (hyroxUserId: number) => Promise<number | null>;
  getHyroxEquipmentIdByName: () => Promise<Map<string, number>>;
  getOneFiitEquipmentNameById: () => Promise<Map<number, string>>;
  getOneFiitInstancePlanId: (instanceId: string) => Promise<string | null>;
  getHyroxActiveInstanceIdForPlan: (planId: string) => Promise<HyroxInstanceMatch>;
}

interface TranslateArgs {
  variables: MirrorVariables;
  hyroxUserId: number;
  lookups: MirrorLookups;
}

type Translator = (args: TranslateArgs) => TranslateResult | Promise<TranslateResult>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isIdList = (value: unknown): value is Array<{ id: number }> =>
  Array.isArray(value) && value.every((entry) => isRecord(entry) && typeof entry.id === 'number');

const skip = (reason: MirrorSkipReason): TranslateResult => ({ ok: false, reason });

const withHyroxUser = ({ variables, hyroxUserId }: TranslateArgs): TranslateResult => {
  const { input } = variables;

  if (!isRecord(input) || !isRecord(input.user)) {
    return skip('unexpected_variables');
  }

  return { ok: true, variables: { ...variables, input: { ...input, user: { id: hyroxUserId } } } };
};

// Retargets the write at the signed-in user's own hyrox gym rather than translating the incoming gym id.
// Both current call sites patch the user's own gym, so the two agree; this stays correct only while that
// holds. A partner-scoped call site that patches some other gym would need the incoming id translated and
// verified here first — mirroring it as-is would write the wrong gym.
const translateGymEquipment = async ({ variables, hyroxUserId, lookups }: TranslateArgs): Promise<TranslateResult> => {
  const { input } = variables;

  if (!isRecord(input) || !isIdList(input.equipment)) {
    return skip('unexpected_variables');
  }

  if ('gymType' in input) {
    return skip('gym_type_not_mirrored');
  }

  const gymId = await lookups.getHyroxGymId(hyroxUserId);

  if (gymId === null) {
    return skip('no_hyrox_gym');
  }

  const [namesById, idsByName] = await Promise.all([
    lookups.getOneFiitEquipmentNameById(),
    lookups.getHyroxEquipmentIdByName(),
  ]);

  // `equipment` replaces the gym's whole set on hyrox, so one untranslatable id means the mirror would
  // silently drop equipment the user never removed — skip the lot instead (decision D6)
  const equipment: Array<{ id: number }> = [];

  for (const { id } of input.equipment) {
    const name = namesById.get(id);
    const hyroxId = name === undefined ? undefined : idsByName.get(name);

    if (hyroxId === undefined) {
      return skip('equipment_untranslatable');
    }

    equipment.push({ id: hyroxId });
  }

  return { ok: true, variables: { ...variables, id: gymId, input: { ...input, equipment } } };
};

const translateCancelProgressivePlan = async ({ variables, lookups }: TranslateArgs): Promise<TranslateResult> => {
  const { input } = variables;

  if (!isRecord(input) || typeof input.progressivePlanInstanceId !== 'string') {
    return skip('unexpected_variables');
  }

  // Instances started after cutover have unrelated ids on each side, so the plan id on the user's gym is
  // what identifies the hyrox row (sub-plan S §3.5). More than one ACTIVE match is refused, not guessed.
  const planId = await lookups.getOneFiitInstancePlanId(input.progressivePlanInstanceId);

  if (planId === null) {
    return skip('no_one_fiit_instance');
  }

  const match = await lookups.getHyroxActiveInstanceIdForPlan(planId);

  if (!match.found) {
    return skip(match.reason);
  }

  return {
    ok: true,
    variables: { ...variables, input: { ...input, progressivePlanInstanceId: match.id } },
  };
};

const passThrough = ({ variables }: TranslateArgs): TranslateResult => ({ ok: true, variables });

// Every hyrox-app user-state mutation, by operation name, mapped to how its variables are translated.
// These keys *are* the allow-list — MIRRORED_OPERATIONS is derived from them — so an operation cannot be
// gated for mirroring without also having a translator, and adding one to a list while forgetting the
// other is not expressible. New writes opt in by adding an entry here.
// PatchGymTypeByGym is deliberately absent (sub-plan S §3.6, decision D3).
const TRANSLATORS: Readonly<Record<string, Translator>> = {
  SaveLessonById: withHyroxUser,
  SaveLessonByIdClassCard: withHyroxUser,
  UnsaveLessonById: withHyroxUser,
  UnsaveLessonByIdClassCard: withHyroxUser,
  CompleteLessonById: withHyroxUser,
  UncompleteLessonById: withHyroxUser,
  StartProgressivePlan: passThrough,
  CancelProgressivePlan: translateCancelProgressivePlan,
  PatchUser: ({ variables, hyroxUserId }) => ({ ok: true, variables: { ...variables, id: hyroxUserId } }),
  UpdatePreferredLanguage: ({ variables, hyroxUserId }) => ({
    ok: true,
    variables: { ...variables, userId: hyroxUserId },
  }),
  PatchGym: translateGymEquipment,
  PatchGymEquipments: translateGymEquipment,
};

export const MIRRORED_OPERATIONS: ReadonlySet<string> = new Set(Object.keys(TRANSLATORS));

export const translateVariables = async (
  operationName: string,
  variables: MirrorVariables,
  hyroxUserId: number,
  lookups: MirrorLookups,
): Promise<TranslateResult> => {
  const translator = TRANSLATORS[operationName];

  // Unreachable through the link, which gates on MIRRORED_OPERATIONS first; kept so a direct caller
  // cannot mirror an un-translated operation by accident.
  if (translator === undefined) {
    return skip('operation_not_mirrored');
  }

  return translator({ variables, hyroxUserId, lookups });
};
